import { useEffect, useState } from "react";
import { Client } from "@stomp/stompjs";
import authService from "@/services/authService";
import orderService from "@/services/orderService";
import { WS_URL } from "@/config/api";

const POLLING_INTERVAL = 10000; // 10 s (só quando o WebSocket não está conectado)

/**
 * Contador de pedidos "Aguardando" da empresa.
 * - Tempo real: STOMP em /topic/admin/orders/{companyCode} (payload = número).
 * - Fallback: polling em GET /orders?status=Aguardando enquanto o socket
 *   estiver desconectado ou quando não houver empresa (owner em "todas").
 * Um ERROR do STOMP (token sem empresa/desatualizado) desliga o socket; o
 * polling seguinte recebe o 401 e o interceptor encerra a sessão.
 *
 * @param {boolean} enabled - usuário tem o menu "orders"
 * @param {string|null} companyCode - empresa efetiva (useAuth().companyCode)
 */
export function usePedidoStatus(enabled = true, companyCode = null) {
  const [pedido, setPedido] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setPedido(0);
      return;
    }

    const token = authService.getToken();
    if (!token) return;

    let disposed = false;
    let intervalId = null;

    const fetchPendingOrders = async () => {
      try {
        const count = await orderService.countPending();
        if (!disposed) setPedido(count);
      } catch (error) {
        // Mantém a contagem anterior em caso de erro
      }
    };

    const startPolling = () => {
      if (intervalId || disposed) return;
      intervalId = setInterval(fetchPendingOrders, POLLING_INTERVAL);
    };

    const stopPolling = () => {
      if (intervalId) clearInterval(intervalId);
      intervalId = null;
    };

    // Contagem inicial (o tópico só publica quando algo muda)
    fetchPendingOrders();

    if (!companyCode) {
      startPolling();
      return () => {
        disposed = true;
        stopPolling();
      };
    }

    const client = new Client({
      brokerURL: `${WS_URL}?access_token=${encodeURIComponent(token)}`,
      connectHeaders: { Authorization: `Bearer ${token}` },
      reconnectDelay: POLLING_INTERVAL,
      heartbeatIncoming: 20000,
      heartbeatOutgoing: 20000,
      debug: () => {},
      onConnect: () => {
        stopPolling();
        client.subscribe(`/topic/admin/orders/${companyCode}`, (message) => {
          const value = Number(message.body);
          if (!Number.isNaN(value)) setPedido(value);
        });
        fetchPendingOrders();
      },
      onStompError: (frame) => {
        console.warn("⚠️ WebSocket recusado:", frame.headers?.message);
        client.deactivate();
        startPolling();
      },
      onWebSocketClose: () => {
        startPolling();
      },
    });

    client.activate();

    return () => {
      disposed = true;
      stopPolling();
      client.deactivate();
    };
  }, [enabled, companyCode]);

  return { pedido };
}
